import { PaginationInfo } from '@scrubtech/models'
import { fetchWithErrors, HTTPMethods, newRequest, parseResponse, token, urlForEndpoint } from './helpers'

/**
 * Base API client to reduce code duplication across API functions
 */
export class BaseApiClient {
  protected resourcePath: string

  constructor(resourcePath: string) {
    this.resourcePath = resourcePath
  }

  /**
   * Get a list of resources
   */
  protected async getList<T, F = any>(
    transformFn: (data: any) => T,
    params: {
      sorting?: string;
      page?: number;
      limit?: number;
      filters?: F;
      search?: string;
      [key: string]: any;
    } = {},
  ): Promise<{ items: T[]; paginationInfo: PaginationInfo; requestParams: any }> {
    const { sorting = 'id', page = 1, limit = 30, filters = {}, search = '', ...otherParams } = params

    // Prepare filters if needed
    const flattenedFilters = this.flattenFilters(filters)

    // Build request
    const url = urlForEndpoint(this.resourcePath, {
      sorting,
      page,
      limit,
      search,
      ...flattenedFilters,
      ...otherParams,
    })

    const request = newRequest(HTTPMethods.GET, token())

    try {
      // Fetch
      const response = await fetchWithErrors(url, request)

      // Parse response
      const data = await parseResponse(response)

      // Extract items using the resource name (e.g., 'customers', 'products')
      const resourceKey = Object.keys(data).find(key =>
        Array.isArray(data[key]) && key !== 'paginationInfo',
      ) || ''

      const items = resourceKey ? data[resourceKey].map(transformFn) : []

      return {
        items,
        paginationInfo: data.paginationInfo,
        requestParams:  {
          sorting,
          page,
          limit,
          filters,
          search,
          ...otherParams,
        },
      }
    } catch (err) {
      throw err  // Let the derived class handle error wrapping
    }
  }

  /**
   * Get a single resource by ID
   */
  protected async getByID<T>(
    id: number | string,
    transformFn: (data: any) => T,
    subPath: string = '',
  ): Promise<T> {
    // Build request
    const path = `${this.resourcePath}/${id}${subPath ? `/${subPath}` : ''}`
    const url = urlForEndpoint(path)
    const request = newRequest(HTTPMethods.GET, token())

    try {
      // Fetch
      const response = await fetchWithErrors(url, request)

      // Parse response
      const data = await parseResponse(response)

      // Extract the resource using the singular form of the resource name
      const resourceKey = Object.keys(data).find(key =>
        typeof data[key] === 'object' && !Array.isArray(data[key]),
      ) || ''

      return transformFn(resourceKey ? data[resourceKey] : data)
    } catch (err) {
      throw err  // Let the derived class handle error wrapping
    }
  }

  /**
   * Create a new resource
   */
  protected async create<T, P = any>(
    payload: P,
    transformFn: (data: any) => T,
    subPath: string = '',
  ): Promise<T> {
    // Build request
    const path = `${this.resourcePath}${subPath ? `/${subPath}` : ''}`
    const url = urlForEndpoint(path)
    const request = newRequest(HTTPMethods.POST, token())
    request.body = JSON.stringify(payload)

    try {
      // Fetch
      const response = await fetchWithErrors(url, request)

      // Parse response
      const data = await parseResponse(response)

      // Extract the resource using the singular form of the resource name
      const resourceKey = Object.keys(data).find(key =>
        typeof data[key] === 'object' && !Array.isArray(data[key]),
      ) || ''

      return transformFn(resourceKey ? data[resourceKey] : data)
    } catch (err) {
      throw err  // Let the derived class handle error wrapping
    }
  }

  /**
   * Update an existing resource
   */
  protected async update<T, P = any>(
    id: number | string,
    payload: P,
    transformFn: (data: any) => T,
    subPath: string = '',
  ): Promise<T> {
    // Build request
    const path = `${this.resourcePath}/${id}${subPath ? `/${subPath}` : ''}`
    const url = urlForEndpoint(path)
    const request = newRequest(HTTPMethods.PUT, token())
    request.body = JSON.stringify(payload)

    try {
      // Fetch
      const response = await fetchWithErrors(url, request)

      // Parse response
      const data = await parseResponse(response)

      // Extract the resource using the singular form of the resource name
      const resourceKey = Object.keys(data).find(key =>
        typeof data[key] === 'object' && !Array.isArray(data[key]),
      ) || ''

      return transformFn(resourceKey ? data[resourceKey] : data)
    } catch (err) {
      throw err  // Let the derived class handle error wrapping
    }
  }

  /**
   * Delete a resource
   */
  protected async delete<T>(
    id: number | string,
    transformFn: (data: any) => T,
    subPath: string = '',
  ): Promise<T> {
    // Build request
    const path = `${this.resourcePath}/${id}${subPath ? `/${subPath}` : ''}`
    const url = urlForEndpoint(path)
    const request = newRequest(HTTPMethods.DELETE, token())

    try {
      // Fetch
      const response = await fetchWithErrors(url, request)

      // Parse response
      const data = await parseResponse(response)

      // Extract the resource using the singular form of the resource name
      const resourceKey = Object.keys(data).find(key =>
        typeof data[key] === 'object' && !Array.isArray(data[key]),
      ) || ''

      return transformFn(resourceKey ? data[resourceKey] : data)
    } catch (err) {
      throw err  // Let the derived class handle error wrapping
    }
  }

  /**
   * Helper method to flatten filters
   */
  protected flattenFilters(filters: any): any {
    if (!filters || typeof filters !== 'object') {
      return {}
    }

    const flattened: any = {}

    for (const [key, value] of Object.entries(filters)) {
      if (value === null || value === undefined) {
        continue
      }

      if (value instanceof Date) {
        flattened[key] = value.toISOString()
      } else if (typeof value === 'object' && 'id' in value) {
        flattened[key] = value.id
      } else if (Array.isArray(value)) {
        flattened[key] = value.map(v => v.id || v).join(',')
      } else {
        flattened[key] = value
      }
    }

    return flattened
  }
}
